import React, {MouseEvent, type ReactElement} from "react";
import {css, toSentenceCase} from "/src/utils/utils";
import {type AmplitudeCtaProps, logEvent} from "/src/utils/amplitude";
import Icon, {IconSVG} from "../../icon/Icon";
import LinkTag from "../link-tag/LinkTag";

import {ButtonProps} from "../button/Button";

import * as styles from "./cta-button.module.scss";

export interface CTA {
  altStyle?: boolean;
  label: string;
  url: string;
  amplitudeCtaProps?: AmplitudeCtaProps;
  amplitudeCtaName?: string;
  amplitudeCtaType?: string;
  iconSrc?: IconSVG;
  /* This property is used in datadog for ui testing */
  dataTag?: string;
  ariaLabel?: string;
  openInNewTab?: boolean;
}

export interface CTAButtonProps
  extends Omit<ButtonProps, "children" | "ariaLabel"> {
  cta: CTA;
  onClick?: (e: MouseEvent<HTMLElement>) => unknown;
  classes?: string;
  iconClasses?: string;
}

export function formatContentfulCTA(
  contentfulCTA: Queries.CtaButtonFragmentFragment
): CTA | undefined {
  if (contentfulCTA.label && contentfulCTA.url) {
    return {
      label: contentfulCTA.label,
      url: contentfulCTA.url,
      amplitudeCtaName: contentfulCTA.amplitudeCtaName || undefined,
      amplitudeCtaType: contentfulCTA.amplitudeCtaType || undefined,
      openInNewTab: contentfulCTA.openInNewTab || false,
    };
  }
}

const CTAButton = (props: CTAButtonProps): ReactElement | null => {
  const {
    cta,
    kind = "primary",
    onClick,
    disabled = false,
    fullWidth = false,
    darkMode = false,
    small = false,
    classes,
    loading = false,
    iconClasses,
  } = props;

  if (cta == null || cta.url == null || cta.label == null) {
    return null;
  }

  const {
    amplitudeCtaProps,
    amplitudeCtaName = "",
    amplitudeCtaType = "",
    label,
    iconSrc = null,
    url = "",
    ariaLabel,
    openInNewTab = false,
  } = cta;

  const ampProps: AmplitudeCtaProps =
    amplitudeCtaProps || ({} as AmplitudeCtaProps);

  // don't overwrite hardcoded values with contentful values if extant
  ampProps.name = ampProps?.name || amplitudeCtaName;
  ampProps.type = ampProps.type || amplitudeCtaType;
  ampProps.linkUrl = ampProps.linkUrl?.length ? ampProps.linkUrl : url;

  const CTAButtonStyles = css(
    styles.button,
    styles[`button${toSentenceCase(kind)}`],
    darkMode ? styles.buttonDarkmode : "",
    fullWidth ? styles.buttonFullWidth : "",
    disabled ? styles.buttonDisabled : "",
    small ? "p-small" : "p",
    small && kind !== "tertiary" ? styles.buttonSmall : "",
    loading ? styles.buttonLoading : "",
    classes!
  );

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    logEvent("click:marketing_site_cta", {
      ...ampProps,
    });

    if (onClick != null) {
      onClick(e);
    }
  };

  return (
    <LinkTag
      classes={CTAButtonStyles}
      href={url}
      onClick={handleClick}
      openInNewTab={openInNewTab}
      ariaLabel={ariaLabel!}
    >
      <span>
        {label}
        {iconSrc && (
          <Icon
            classes={css(styles.ctaButton__icon, iconClasses!)}
            svg={iconSrc}
            role="presentation"
          />
        )}
      </span>
    </LinkTag>
  );
};

export default CTAButton;
